import React, { useEffect, useState, useContext, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ExpenseChart } from '../components/charts/ExpenseChart';
import { ExpenseStatusBadge } from '../components/ui/ExpenseStatusBadge';
import { ExpenseDetailModal } from '../components/modals/ExpenseDetailModal';
import { Pagination } from '../components/ui/Pagination';
import { AuthContext } from '../App';
import { DollarSignIcon, CheckCircleIcon, ClockIcon, XCircleIcon, PlusIcon, ArrowRightIcon, FileTextIcon, CalendarIcon, ChevronDownIcon, ChevronUpIcon, AlertTriangleIcon, InfoIcon } from 'lucide-react';
import { mockExpenses } from '../utils/mockData';
import { formatDate, isValidDate, parseDate } from '../utils/dateUtils';
import { calculateTotalAmount, filterExpensesByDateRange, getExpenseStatsByStatus, formatExpensesForChart } from '../utils/dashboardUtils';
import { formatCurrency, formatDateTime } from '../utils/formatUtils';
import { ChartSkeleton } from '../components/charts/ChartSkeleton';
export function EmployeeDashboard() {
  const {
    userInfo
  } = useContext(AuthContext);
  // State for date range filter
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  // Filter expenses for current user
  const userExpenses = mockExpenses.filter(expense => expense.employeeId === userInfo.id);
  // State for filtered expenses and stats
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [expenseStats, setExpenseStats] = useState({
    pending: {
      count: 0,
      amount: 0
    },
    approved: {
      count: 0,
      amount: 0
    },
    rejected: {
      count: 0,
      amount: 0
    },
    total: {
      count: 0,
      amount: 0
    }
  });
  // State for expense chart data
  const [chartData, setChartData] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  // State for selected expense and modal
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [paginatedExpenses, setPaginatedExpenses] = useState([]);
  // Add loading state for chart
  const [isTyping, setIsTyping] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState(['per diem rates', 'receipt requirements', 'approval workflow']);
  // Apply date range filter
  useEffect(() => {
    // Validate dates before filtering
    const start = parseDate(dateRange.startDate);
    const end = parseDate(dateRange.endDate);
    if (!start || !end) {
      console.error('Invalid date range', {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      return;
    }
    // Filter expenses by date range
    const filtered = filterExpensesByDateRange(userExpenses, dateRange.startDate, dateRange.endDate);
    setFilteredExpenses(filtered);
    // Calculate expense statistics
    const stats = getExpenseStatsByStatus(filtered);
    setExpenseStats(stats);
    // Add loading state for chart data
    setChartLoading(true);
    // Simulate network delay for chart data
    setTimeout(() => {
      // Prepare chart data
      const chartDataByType = formatExpensesForChart(filtered, 'type');
      setChartData(chartDataByType);
      setChartLoading(false);
    }, 600);
    // Calculate total pages for pagination
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [dateRange, userExpenses, userInfo.id, itemsPerPage]);
  // Update paginated expenses when page changes
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedExpenses(filteredExpenses.slice(startIndex, endIndex));
  }, [filteredExpenses, currentPage, itemsPerPage]);
  // Handle date range change
  const handleDateRangeChange = e => {
    const {
      name,
      value
    } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Toggle row expansion
  const toggleRowExpansion = id => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  // Open expense detail modal
  const openExpenseDetail = expense => {
    setSelectedExpense(expense);
    setIsDetailModalOpen(true);
  };
  // Generate quick filters for date ranges
  const applyQuickFilter = days => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - days);
    setDateRange({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    });
  };
  // Handle page change
  const handlePageChange = page => {
    setCurrentPage(page);
  };
  // Handle items per page change
  const handleItemsPerPageChange = e => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
  };
  // Get notification message based on expense stats
  const getNotificationMessage = () => {
    if (expenseStats.pending.count > 0) {
      return {
        type: 'info',
        icon: <InfoIcon size={20} className="text-blue-400" data-id="element-1342" />,
        message: `You have ${expenseStats.pending.count} pending expense${expenseStats.pending.count === 1 ? '' : 's'} awaiting approval.`
      };
    }
    if (expenseStats.rejected.count > 0) {
      return {
        type: 'warning',
        icon: <AlertTriangleIcon size={20} className="text-yellow-400" data-id="element-1343" />,
        message: `You have ${expenseStats.rejected.count} rejected expense${expenseStats.rejected.count === 1 ? '' : 's'}. Please review and resubmit if needed.`
      };
    }
    return null;
  };
  const notification = getNotificationMessage();
  return <div className="space-y-6" data-id="element-1344">
      {/* Dashboard Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-1345">
        <div data-id="element-1346">
          <h1 className="text-2xl font-bold text-gray-100" data-id="element-1347">Your Dashboard</h1>
          <p className="text-gray-400 mt-1" data-id="element-1348">
            Track and manage your expense submissions
          </p>
        </div>
        <Link to="/expense/new" data-id="element-1349">
          <Button data-id="element-1350">
            <PlusIcon size={16} className="mr-1.5" data-id="element-1351" />
            New Expense
          </Button>
        </Link>
      </div>
      {/* Notification */}
      {notification && <div className={`p-4 rounded-md border ${notification.type === 'warning' ? 'bg-yellow-900/30 border-yellow-800' : 'bg-blue-900/30 border-blue-800'}`} data-id="element-1352">
          <div className="flex" data-id="element-1353">
            <div className="flex-shrink-0" data-id="element-1354">{notification.icon}</div>
            <div className="ml-3" data-id="element-1355">
              <p className={`text-sm font-medium ${notification.type === 'warning' ? 'text-yellow-300' : 'text-blue-300'}`} data-id="element-1356">
                {notification.message}
              </p>
            </div>
          </div>
        </div>}
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1357">
        <Card className="p-4" data-id="element-1358">
          <div className="flex items-center" data-id="element-1359">
            <div className="h-12 w-12 rounded-full bg-blue-900/30 flex items-center justify-center mr-4" data-id="element-1360">
              <DollarSignIcon size={24} className="text-blue-400" data-id="element-1361" />
            </div>
            <div data-id="element-1362">
              <p className="text-sm text-gray-400" data-id="element-1363">Total Expenses</p>
              <p className="text-2xl font-bold text-gray-100" data-id="element-1364">
                {formatCurrency(expenseStats.total.amount)}
              </p>
              <p className="text-xs text-gray-400" data-id="element-1365">
                {expenseStats.total.count} expenses
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-id="element-1366">
          <div className="flex items-center" data-id="element-1367">
            <div className="h-12 w-12 rounded-full bg-yellow-900/30 flex items-center justify-center mr-4" data-id="element-1368">
              <ClockIcon size={24} className="text-yellow-400" data-id="element-1369" />
            </div>
            <div data-id="element-1370">
              <p className="text-sm text-gray-400" data-id="element-1371">Pending</p>
              <p className="text-2xl font-bold text-gray-100" data-id="element-1372">
                {formatCurrency(expenseStats.pending.amount)}
              </p>
              <p className="text-xs text-gray-400" data-id="element-1373">
                {expenseStats.pending.count} expenses
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-id="element-1374">
          <div className="flex items-center" data-id="element-1375">
            <div className="h-12 w-12 rounded-full bg-green-900/30 flex items-center justify-center mr-4" data-id="element-1376">
              <CheckCircleIcon size={24} className="text-green-400" data-id="element-1377" />
            </div>
            <div data-id="element-1378">
              <p className="text-sm text-gray-400" data-id="element-1379">Approved</p>
              <p className="text-2xl font-bold text-gray-100" data-id="element-1380">
                {formatCurrency(expenseStats.approved.amount)}
              </p>
              <p className="text-xs text-gray-400" data-id="element-1381">
                {expenseStats.approved.count} expenses
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-id="element-1382">
          <div className="flex items-center" data-id="element-1383">
            <div className="h-12 w-12 rounded-full bg-red-900/30 flex items-center justify-center mr-4" data-id="element-1384">
              <XCircleIcon size={24} className="text-red-400" data-id="element-1385" />
            </div>
            <div data-id="element-1386">
              <p className="text-sm text-gray-400" data-id="element-1387">Rejected</p>
              <p className="text-2xl font-bold text-gray-100" data-id="element-1388">
                {formatCurrency(expenseStats.rejected.amount)}
              </p>
              <p className="text-xs text-gray-400" data-id="element-1389">
                {expenseStats.rejected.count} expenses
              </p>
            </div>
          </div>
        </Card>
      </div>
      {/* Expense Chart */}
      <Card data-id="element-1390">
        <div className="p-4 border-b border-gray-700" data-id="element-1391">
          <h2 className="text-lg font-medium text-gray-100" data-id="element-1392">
            Expenses by Category
          </h2>
        </div>
        <div className="p-4" data-id="element-1393">
          {chartLoading ? <ChartSkeleton variant="pie" data-id="element-1394" /> : <ExpenseChart data={chartData} title="Your expense distribution" showControls={true} data-id="element-1395" />}
        </div>
      </Card>
      {/* Recent Expenses */}
      <Card data-id="element-1396">
        <div className="p-4 border-b border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1397">
          <h2 className="text-lg font-medium text-gray-100" data-id="element-1398">Recent Expenses</h2>
          <div className="flex flex-wrap gap-2" data-id="element-1399">
            <button onClick={() => applyQuickFilter(7)} className="px-3 py-1 text-xs rounded-md bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-650" data-id="element-1400">
              Last 7 days
            </button>
            <button onClick={() => applyQuickFilter(30)} className="px-3 py-1 text-xs rounded-md bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-650" data-id="element-1401">
              Last 30 days
            </button>
            <button onClick={() => applyQuickFilter(90)} className="px-3 py-1 text-xs rounded-md bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-650" data-id="element-1402">
              Last 90 days
            </button>
          </div>
        </div>
        {/* Date Range Filter */}
        <div className="p-4 border-b border-gray-700 bg-gray-750" data-id="element-1403">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-1404">
            <div className="flex-1" data-id="element-1405">
              <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-1406">
                Start Date
              </label>
              <div className="relative" data-id="element-1407">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1408">
                  <CalendarIcon size={16} className="text-gray-400" data-id="element-1409" />
                </div>
                <input type="date" name="startDate" value={dateRange.startDate} onChange={handleDateRangeChange} className="pl-10 w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200 text-sm" data-id="element-1410" />
              </div>
            </div>
            <div className="flex-1" data-id="element-1411">
              <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-1412">
                End Date
              </label>
              <div className="relative" data-id="element-1413">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1414">
                  <CalendarIcon size={16} className="text-gray-400" data-id="element-1415" />
                </div>
                <input type="date" name="endDate" value={dateRange.endDate} onChange={handleDateRangeChange} className="pl-10 w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200 text-sm" data-id="element-1416" />
              </div>
            </div>
          </div>
        </div>
        {/* Items per page selector */}
        <div className="p-4 border-b border-gray-700 bg-gray-750 flex justify-end" data-id="element-1417">
          <div className="flex items-center space-x-2" data-id="element-1418">
            <label htmlFor="itemsPerPage" className="text-sm text-gray-400" data-id="element-1419">
              Show:
            </label>
            <select id="itemsPerPage" value={itemsPerPage} onChange={handleItemsPerPageChange} className="bg-gray-700 border border-gray-600 text-gray-200 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 px-2 py-1" data-id="element-1420">
              <option value="10" data-id="element-1421">10</option>
              <option value="25" data-id="element-1422">25</option>
              <option value="50" data-id="element-1423">50</option>
              <option value="100" data-id="element-1424">100</option>
            </select>
          </div>
        </div>
        {/* Expenses Table */}
        <div className="overflow-x-auto" data-id="element-1425">
          <table className="min-w-full divide-y divide-gray-700" data-id="element-1426">
            <thead className="bg-gray-800" data-id="element-1427">
              <tr data-id="element-1428">
                <th className="w-10 px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-1429"></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-1430">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-1431">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-1432">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-1433">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-1434">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700" data-id="element-1435">
              {paginatedExpenses.length > 0 ? paginatedExpenses.map(expense => <Fragment key={expense.id} data-id="element-1436">
                    <tr className={`hover:bg-gray-700 transition-colors ${expandedRows[expense.id] ? 'bg-gray-750' : ''} cursor-pointer`} onClick={() => openExpenseDetail(expense)} data-id="element-1437">
                      <td className="px-4 py-4 whitespace-nowrap" onClick={e => {
                  e.stopPropagation();
                  toggleRowExpansion(expense.id);
                }} data-id="element-1438">
                        <button className="text-gray-400 hover:text-gray-200" data-id="element-1439">
                          {expandedRows[expense.id] ? <ChevronUpIcon size={16} data-id="element-1440" /> : <ChevronDownIcon size={16} data-id="element-1441" />}
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300" data-id="element-1442">
                        {formatDate(expense.date)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300 capitalize" data-id="element-1443">
                        {expense.type}
                        {expense.subtype && <span className="text-gray-400" data-id="element-1444">
                            {' '}
                            ({expense.subtype})
                          </span>}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-200" data-id="element-1445">
                        {formatCurrency(expense.amount)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap" data-id="element-1446">
                        <ExpenseStatusBadge status={expense.status} data-id="element-1447" />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={e => e.stopPropagation()} data-id="element-1448">
                        <div className="flex items-center justify-end space-x-2" data-id="element-1449">
                          <button className={`px-2 py-1 rounded text-xs font-medium ${expense.status === 'rejected' ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' : 'bg-gray-700 text-gray-400'} transition-colors`} disabled={expense.status !== 'rejected'} data-id="element-1450">
                            Resubmit
                          </button>
                          <button className="px-2 py-1 rounded text-xs font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors" data-id="element-1451">
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedRows[expense.id] && <tr className="bg-gray-750" data-id="element-1452">
                        <td colSpan={6} className="px-4 py-3" data-id="element-1453">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-2" data-id="element-1454">
                            <div data-id="element-1455">
                              <h4 className="text-sm font-medium text-gray-300 mb-2" data-id="element-1456">
                                Expense Details
                              </h4>
                              <div className="space-y-2" data-id="element-1457">
                                <div className="flex" data-id="element-1458">
                                  <span className="text-xs text-gray-400 w-24" data-id="element-1459">
                                    Description:
                                  </span>
                                  <span className="text-xs text-gray-200" data-id="element-1460">
                                    {expense.description}
                                  </span>
                                </div>
                                <div className="flex" data-id="element-1461">
                                  <span className="text-xs text-gray-400 w-24" data-id="element-1462">
                                    Location:
                                  </span>
                                  <span className="text-xs text-gray-200" data-id="element-1463">
                                    {expense.location || 'N/A'}
                                  </span>
                                </div>
                                <div className="flex" data-id="element-1464">
                                  <span className="text-xs text-gray-400 w-24" data-id="element-1465">
                                    Expense Date:
                                  </span>
                                  <span className="text-xs text-gray-200" data-id="element-1466">
                                    {formatDateTime(expense.date, true)}
                                  </span>
                                </div>
                                <div className="flex" data-id="element-1467">
                                  <span className="text-xs text-gray-400 w-24" data-id="element-1468">
                                    Submitted:
                                  </span>
                                  <span className="text-xs text-gray-200" data-id="element-1469">
                                    {formatDateTime(expense.submittedDate, true)}
                                  </span>
                                </div>
                                {expense.status === 'rejected' && <div className="flex" data-id="element-1470">
                                    <span className="text-xs text-gray-400 w-24" data-id="element-1471">
                                      Reason:
                                    </span>
                                    <span className="text-xs text-red-300" data-id="element-1472">
                                      {expense.rejectionReason || 'No reason provided'}
                                    </span>
                                  </div>}
                              </div>
                            </div>
                            <div data-id="element-1473">
                              <h4 className="text-sm font-medium text-gray-300 mb-2" data-id="element-1474">
                                Receipt
                              </h4>
                              {expense.receiptUrl ? <div className="border border-gray-600 rounded-md p-2 bg-gray-700 flex items-center justify-center h-24" data-id="element-1475">
                                  <img src={expense.receiptUrl} alt="Receipt" className="max-h-full" data-id="element-1476" />
                                </div> : <div className="border border-gray-600 rounded-md p-2 bg-gray-700 flex items-center justify-center h-24 text-gray-400 text-xs" data-id="element-1477">
                                  No receipt provided
                                </div>}
                            </div>
                          </div>
                        </td>
                      </tr>}
                  </Fragment>) : <tr data-id="element-1478">
                  <td colSpan={6} className="px-4 py-6 text-center text-gray-400" data-id="element-1479">
                    No expenses found for the selected date range
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {filteredExpenses.length > 0 && <div className="px-4 py-3 border-t border-gray-700 flex justify-between items-center flex-wrap gap-4" data-id="element-1480">
            <div className="text-sm text-gray-400" data-id="element-1481">
              Showing{' '}
              {Math.min(filteredExpenses.length, (currentPage - 1) * itemsPerPage + 1)}{' '}
              to {Math.min(currentPage * itemsPerPage, filteredExpenses.length)}{' '}
              of {filteredExpenses.length} expenses
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} data-id="element-1482" />
            {filteredExpenses.length > itemsPerPage && <Link to="/expenses/history" className="text-blue-400 hover:text-blue-300 text-sm flex items-center ml-auto" data-id="element-1483">
                View All Expenses
                <ArrowRightIcon size={16} className="ml-1" data-id="element-1484" />
              </Link>}
          </div>}
      </Card>
      {/* Expense Detail Modal */}
      {selectedExpense && <ExpenseDetailModal isOpen={isDetailModalOpen} onClose={() => setIsDetailModalOpen(false)} expense={selectedExpense} isAdmin={false} data-id="element-1485" />}
    </div>;
}